<template>
  <div class="desktop">
    <div ref="videoContainer" class="video-container">
      <div v-show="countDown > 0" class="countdown-overlay">
        <span class="countdown-number">{{ countDown }}</span>
        <Button :onclick="() => handleCancelRecordingCountdown()" variant="primary">{{ t('CANCEL') }}</Button>
      </div>
      <div v-show="!videoPreviewVisible && isRecording" class="elapsed-time-overlay" :title="t('ELAPSED TIME')">
        <font-awesome-icon :icon="['far', 'clock']" />
        <span>{{ elapsedTime }} / {{ formattedVideoMaxDuration }}</span>
      </div>
      <div v-show="!videoPreviewVisible && videoLoaded" class="interactions-overlay">
        <button
          v-if="!isRecording"
          class="interactions-overlay-cog"
          id="interactions-overlay-cog"
          @click="toggleSettings"
          :title="t('SETTINGS')"
        >
          <font-awesome-icon :icon="['fas', 'cog']" class="icon cog" />
        </button>
        <transition name="fade">
          <div ref="deviceOptions" v-show="settingsVisible" class="device-options-container">
            <div class="device-select">
              <div class="icon-container">
                <font-awesome-icon :icon="['fas', 'video']" class="icon video" />
              </div>
              <select
                :title="t('{0} DEVICE SELECTOR', [t('VIDEO')])"
                :disabled="countDown > 0 || isRecording"
                :onchange="(e) => handleVideoDeviceChanged(e)"
              >
                <option v-for="device in videoInputDevices" :key="device.deviceId" :value="device.deviceId">
                  {{ device.label }}
                </option>
              </select>
            </div>
            <div class="device-select">
              <div class="icon-container">
                <font-awesome-icon :icon="['fas', 'microphone']" class="icon microphone" />
              </div>
              <select
                :title="t('{0} DEVICE SELECTOR', [t('AUDIO')])"
                :disabled="countDown > 0 || isRecording"
                :onchange="(e) => handleAudioDeviceChanged(e)"
              >
                <option v-for="device in audioInputDevices" :key="device.deviceId" :value="device.deviceId">
                  {{ device.label }}
                </option>
              </select>
            </div>
          </div>
        </transition>
        <button
          v-if="!isRecording"
          id="interactions-overlay-circle"
          class="interactions-overlay-circle"
          @keyup.space="startCountDownTimer"
          @click="startCountDownTimer"
          :title="t('START RECORDING')"
        >
          <font-awesome-icon :icon="['fas', 'circle']" class="icon circle" />
        </button>
        <button
          v-if="isRecording"
          id="interactions-overlay-square"
          class="interactions-overlay-square"
          @keyup.space="handleStopRecording"
          @click="handleStopRecording"
          :title="t('STOP RECORDING')"
        >
          <CircularProgress :progress="progress" class="circular-progress" />
          <font-awesome-icon :icon="['fas', 'square']" class="icon square" />
        </button>
        <button
          v-if="!isRecording"
          id="interactions-overlay-attach"
          class="interactions-overlay-attach"
          @click="() => handleAttachVideo()"
          :title="t('ATTACH VIDEO')"
        >
          <font-awesome-icon :icon="['fas', 'paperclip']" class="icon paperclip" />
        </button>
      </div>
      <GuidelineOverlay v-if="!videoLoadError && showGuidelines" />
      <VideoPlaceholder v-if="!videoLoaded" :video-load-error="videoLoadError" />
      <video
        v-show="!videoPreviewVisible && videoLoaded"
        ref="desktopVideoElement"
        id="desktop-player"
        class="placeholder-video"
        autoplay
        muted
        playsinline
      >
        Your browser does not support the <code>video</code> element.
      </video>
      <video
        v-show="videoPreviewVisible"
        ref="desktopRecordedVideoElement"
        id="recorded-video"
        class="recorded-video"
        controls
        disablePictureInPicture
        controlsList="nodownload"
      ></video>
    </div>
    <TermsAndConditionsCheck
      v-if="
        (isIframed && !iframeParentIsAdminApp && !clickWrapFeatureEnabled) ||
        (!isIframed && !userInformationNeeded && !videoLoadError && !clickWrapFeatureEnabled)
      "
    />
    <input ref="fileLoader" type="file" :accept="acceptedVideoTypes" @change="(e) => loadVideo(e)" hidden />
    <div v-show="videoPreviewVisible" class="button-container">
      <Button
        :title="t('UPLOAD VIDEO')"
        :disabled="countDown > 0 || isLoading"
        id="btn-upload"
        :onclick="handleUploadRecording"
        variant="primary"
        >{{ t('UPLOAD VIDEO') }}
      </Button>
      <Button
        :title="t('START NEW RECORDING')"
        v-if="restartButtonVisible"
        :disabled="!!videoLoadError || countDown > 0 || (isRecording && !isPaused)"
        id="btn-restart"
        :onclick="startNewRecording"
        variant="secondary"
        >{{ t('START NEW RECORDING') }}
      </Button>
      <a ref="downloadableRecordedVideoElement" :title="t('DOWNLOAD VIDEO')" download class="btn secondary download">
        <font-awesome-icon :icon="['fas', 'download']" class="icon" />
      </a>
    </div>
  </div>
</template>

<script lang="ts">
/// <reference types="@types/dom-mediacapture-record" />
import Button from '@/components/Button.vue';
import TermsAndConditionsCheck from '@/components/TermsAndConditionsCheck.vue';
import { AppFreeAnswerParameters, Client, Question } from 'altru-types';
import { computed, onBeforeUnmount, onMounted, PropType, Ref, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { permissionDeniedMessage, SetupProps, SetupReturnType, VideoLoadError } from '@/types/desktop';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';
import Timer from 'easytimer.js';
import GuidelineOverlay from '@/components/GuidelineOverlay.vue';
import CircularProgress from '@/components/CircularProgress.vue';
import isAcceptedVideoType, { ACCEPTED_TYPES, USER_READABLE_ACCEPTED_TYPES } from '@/utils/isAcceptedVideoType';
import { getConnectedDevices, getUserMedia, hasGetUserMedia, revokeMediaAccess } from '@/utils/userMedia';
import VideoPlaceholder from '@/components/VideoPlaceholder.vue';

export default {
  name: 'Desktop',
  props: {
    uploadVideo: {
      type: Function as PropType<(video: File) => void>,
    },
  },
  components: {
    VideoPlaceholder,
    CircularProgress,
    GuidelineOverlay,
    Button,
    TermsAndConditionsCheck,
  },
  setup(props: SetupProps): SetupReturnType {
    const { t } = useI18n();
    const { getters, dispatch } = useStore();
    const userInformationNeeded: Ref<boolean> = computed(() => getters['clients/userInformationNeeded']);
    const questionInfo: Ref<Question> = computed(() => getters['questions/questionInfo']);
    const isIframed: Ref<boolean> = computed(() => getters['status/isIframed']);
    const iframeSource: Ref<AppFreeAnswerParameters['source']> = computed(() => getters['status/iframeSource']);
    const formattedVideoMaxDuration: Ref<string> | string = computed(() =>
      questionInfo.value.max_duration
        ? new Date(questionInfo.value.max_duration * 1000).toISOString().substr(14, 5)
        : '00:00',
    );
    const isLoading: Ref<boolean> = computed(() => getters['ui/isLoading']);
    const showGuidelines: Ref<boolean> = ref(true);
    const iframeParentIsAdminApp: Ref<boolean> = computed(() => getters['status/iframeParentIsAdminApp']);
    const client: Ref<Client | null> = computed(() => getters['clients/workspaceInfo']);
    const clickWrapFeatureEnabled: Ref<boolean> = computed(
      () => client.value?.feature_keys?.includes('click_wrap_terms_and_conditions') ?? false,
    );

    const videoContainer: Ref<HTMLDivElement | null> = ref<HTMLDivElement | null>(null);
    const desktopVideoElement: Ref<HTMLVideoElement | null> = ref<HTMLVideoElement | null>(null);
    const desktopRecordedVideoElement: Ref<HTMLVideoElement | null> = ref<HTMLVideoElement | null>(null);
    const downloadableRecordedVideoElement: Ref<HTMLAnchorElement | null> = ref<HTMLAnchorElement | null>(null);

    const videoInputDevices: Ref<{ deviceId: string; label: string }[]> = ref([]);
    const audioInputDevices: Ref<{ deviceId: string; label: string }[]> = ref([]);
    const selectedVideoDevice: Ref<string> = ref('');
    const selectedAudioDevice: Ref<string> = ref('');

    const countDown: Ref<number> = ref(-1);
    const startButtonVisible: Ref<boolean> = ref(true);
    const resumeButtonVisible: Ref<boolean> = ref(false);
    const restartButtonVisible: Ref<boolean> = ref(false);

    const videoPreviewVisible: Ref<boolean> = ref(false);
    const isPaused: Ref<boolean> = ref(false);
    const isRecording: Ref<boolean> = ref(false);
    const mediaRecorder: Ref<MediaRecorder | undefined> = ref(undefined);
    const defaultRecordingState: RecordingState = 'inactive';
    const recordingStatus: Ref<RecordingState> = ref(defaultRecordingState);
    const chunks: Ref<any> = ref([]);
    const videoLoaded = ref(false);
    const videoLoadError: VideoLoadError = ref(null);
    const blob: Ref<Blob | null> = ref(new Blob());
    const attachedFile: Ref<File | null> = ref(null);
    const timer = new Timer();
    const elapsedTime: Ref<string> = ref('00:00');
    const progress: Ref<number> = ref(0);

    onMounted(async () => await initializeUserMedia());

    onBeforeUnmount(() => revokeMediaAccess());

    function handleError(error: any) {
      videoLoadError.value = {
        title: 'Error',
        description: error ? error : permissionDeniedMessage,
      };
      console.error('Error: ', error ?? permissionDeniedMessage);
    }

    const attachStreamToVideoElement = (stream: MediaStream): void => {
      if (window) {
        (window as any).stream = stream; // make stream available to console
      }

      selectedVideoDevice.value = selectedVideoDevice.value ?? videoInputDevices.value[0]?.deviceId;
      selectedAudioDevice.value = selectedAudioDevice.value ?? audioInputDevices.value[0]?.deviceId;

      mediaRecorder.value = new MediaRecorder(stream);
      recordingStatus.value = mediaRecorder.value?.state;

      if (desktopVideoElement.value) {
        if ('srcObject' in desktopVideoElement.value) {
          desktopVideoElement.value.srcObject = stream;
          videoLoaded.value = true;
        } else {
          (desktopVideoElement.value as HTMLVideoElement).src = window.URL.createObjectURL(stream);
        }
      }

      if (mediaRecorder.value) {
        mediaRecorder.value.ondataavailable = (ev: any) => {
          chunks.value.push(ev.data as any);
        };

        mediaRecorder.value.onstop = () => {
          attachedFile.value = null;
          blob.value = new Blob(chunks.value, {
            type: mediaRecorder.value?.mimeType || (chunks.value[0] as any).type || 'video/webm',
          });
          chunks.value = [];

          if (desktopRecordedVideoElement.value) {
            desktopRecordedVideoElement.value.src = window.URL.createObjectURL(blob.value);
            if (downloadableRecordedVideoElement.value) {
              downloadableRecordedVideoElement.value.href = desktopRecordedVideoElement.value?.src;
              downloadableRecordedVideoElement.value.download = 'RecordedVideo.webm';
            }
          }
        };
      }
    };

    async function getStream(): Promise<void> {
      const audioSource = selectedAudioDevice.value ?? audioInputDevices.value[0]?.deviceId;
      const videoSource = selectedVideoDevice.value ?? videoInputDevices.value[0]?.deviceId;
      const constraints = {
        ...(audioSource !== '' ? { audio: { deviceId: { exact: audioSource } } } : { audio: true }),
        ...(videoSource !== '' ? { video: { deviceId: { exact: videoSource } } } : { video: true }),
      };
      const stream = await getUserMedia(constraints);

      if (stream) {
        return attachStreamToVideoElement(stream);
      } else {
        return handleError('Unable to get a media stream');
      }
    }

    function buildConnectedDevicesDropdown(devices: MediaDeviceInfo[]) {
      (window as any).deviceInfos = devices; // make available to console
      if (process.env.NODE_ENV !== 'production') {
        console.log('Available input and output devices:', devices);
      }
      devices.map(({ kind, deviceId, label }) => {
        switch (kind) {
          case 'audioinput':
            audioInputDevices.value = [...audioInputDevices.value, { deviceId, label }];
            break;
          case 'videoinput':
            videoInputDevices.value = [...videoInputDevices.value, { deviceId, label }];
            break;
        }
      });
    }

    const initializeUserMedia = async () => {
      if (hasGetUserMedia()) {
        // getStream().then(getDevices).then(gotDevices);
        const stream = await getUserMedia();
        if (stream) {
          // We need to call gUM before gCD on safari, or it only shows the default devices
          const devices = await getConnectedDevices();
          buildConnectedDevicesDropdown(devices);
          attachStreamToVideoElement(stream);
        } else {
          return handleError('Unable to get a media stream');
        }
      } else {
        handleError('getUserMedia() is not supported by your browser');
      }
    };

    const handleStartRecording = (): void => {
      if (isRecording.value) {
        isPaused.value = false;
        mediaRecorder.value?.resume();
      } else {
        isRecording.value = true;
        isPaused.value = false;
        timer.start({
          precision: 'secondTenths',
          callback: (timer) => {
            elapsedTime.value = timer.getTimeValues().toString(['minutes', 'seconds']);
            if (Number(progress.value) >= 100) {
              return handleStopRecording();
            }
            if (questionInfo.value.max_duration) {
              progress.value =
                (Number(
                  `${timer.getTimeValues().minutes * 60 + timer.getTimeValues().seconds}.${
                    timer.getTimeValues().secondTenths
                  }`,
                ) *
                  100) /
                questionInfo.value.max_duration;
            }
          },
        });
        mediaRecorder.value?.start();
      }
      recordingStatus.value = mediaRecorder.value?.state ?? defaultRecordingState;
    };

    const handlePauseRecording = (): void => {
      startButtonVisible.value = false;
      resumeButtonVisible.value = true;
      restartButtonVisible.value = false;
      isRecording.value = true;
      isPaused.value = true;
      mediaRecorder.value?.pause();
      recordingStatus.value = mediaRecorder.value?.state ?? defaultRecordingState;
    };

    const handleStopRecording = (): void => {
      startButtonVisible.value = false;
      resumeButtonVisible.value = false;
      restartButtonVisible.value = true;
      isRecording.value = false;
      mediaRecorder.value?.stop();
      recordingStatus.value = mediaRecorder.value?.state ?? defaultRecordingState;
      videoPreviewVisible.value = true;
      timer.stop();
      revokeMediaAccess();
    };

    /* Upload video prop */
    const handleUploadRecording = async (): Promise<void> => {
      if (blob.value) {
        const file = new File(
          [blob.value],
          `altru-video-${questionInfo.value.id}-${Date.now()}${
            iframeSource.value ? `-${iframeSource.value}` : ''
          }.webm`,
        );
        return props.uploadVideo(file);
      }
      if (attachedFile.value) {
        return props.uploadVideo(attachedFile.value);
      } else {
        await dispatch('ui/setErrorMessage', { value: t('NO VIDEO FOUND') });
      }
    };

    /* Recording countdown */
    const countdownInterval = ref(setInterval(() => null));

    const startCountDownTimer = async (): Promise<void> => {
      showGuidelines.value = false;
      if (!(window as any).stream) {
        await getStream();
      }
      restartButtonVisible.value = false;
      settingsVisible.value = false;
      countDown.value = 3;
      countdownInterval.value = setInterval(() => {
        countDown.value -= 1;
      }, 1000);
    };

    const startNewRecording = async () => {
      await dispatch('ui/toggleLoading', { value: true });
      await getStream();
      await dispatch('ui/toggleLoading', { value: false });
      await startCountDownTimer();
    };

    watch(
      () => countDown.value,
      (current) => {
        videoPreviewVisible.value = false;
        if (current === 0) {
          clearInterval(countdownInterval.value);
          handleStartRecording();
        }
        if (current < 0) {
          clearInterval(countdownInterval.value);
        }
      },
      {
        immediate: true,
      },
    );

    const handleCancelRecordingCountdown = (): void => {
      countDown.value = -1;
      clearInterval(countdownInterval.value);
    };

    /* Devices list */
    const handleVideoDeviceChanged = (e: Event) => {
      selectedVideoDevice.value = (e.target as HTMLSelectElement).value;
      return getStream();
    };

    const handleAudioDeviceChanged = (e: Event) => {
      selectedAudioDevice.value = (e.target as HTMLSelectElement).value;
      return getStream();
    };

    /* Load video */
    const loadVideo = (e: Event): void | Promise<void> => {
      const files = (e.target as HTMLInputElement).files;
      const video: Ref<File> = ref(new File([], ''));
      if (files && files.length > 0) {
        blob.value = null;
        video.value = files[0];
        attachedFile.value = video.value;
        if (!isAcceptedVideoType(video.value.type)) {
          return dispatch('ui/setErrorMessage', {
            value: t('NOT SUPPORTED VIDEO FORMAT {0}', [acceptedVideoList]),
          });
        }
        if (desktopRecordedVideoElement.value) {
          desktopRecordedVideoElement.value.src = window.URL.createObjectURL(video.value);
          videoLoaded.value = true;
          videoPreviewVisible.value = true;
          restartButtonVisible.value = true;

          if (downloadableRecordedVideoElement.value) {
            downloadableRecordedVideoElement.value.href = desktopRecordedVideoElement.value?.src;
          }
        }
      }
    };

    /* Video overlay interactions */
    const settingsVisible = ref(false);
    const deviceOptions: Ref<HTMLDivElement | null> = ref(null);
    onClickOutside(deviceOptions, (e: Event) => {
      const elementType = (e.target as any).tagName;
      const elementClickedOnId = (e.target as any)?.id;
      if (elementType !== 'path' && elementType !== 'svg') {
        if (elementClickedOnId !== 'interactions-overlay-cog') {
          settingsVisible.value = false;
        }
      }
    });

    const toggleSettings = (e: Event): void => {
      e.stopPropagation();
      settingsVisible.value = !settingsVisible.value;
    };

    const fileLoader: Ref<HTMLInputElement | null> = ref(null);
    const handleAttachVideo = () => {
      showGuidelines.value = false;
      fileLoader.value?.click();
    };

    const acceptedVideoTypes = ACCEPTED_TYPES.join(',');
    const acceptedVideoList = USER_READABLE_ACCEPTED_TYPES.join(', ');

    return {
      videoContainer,
      desktopVideoElement,
      desktopRecordedVideoElement,
      videoLoadError,
      isPaused,
      countDown,
      videoPreviewVisible,
      videoInputDevices,
      audioInputDevices,
      isLoading,
      isRecording,
      videoLoaded,
      handlePauseRecording,
      handleStopRecording,
      handleUploadRecording,
      startCountDownTimer,
      startNewRecording,
      handleCancelRecordingCountdown,
      handleVideoDeviceChanged,
      handleAudioDeviceChanged,
      loadVideo,
      t,
      startButtonVisible,
      resumeButtonVisible,
      restartButtonVisible,
      toggleSettings,
      settingsVisible,
      deviceOptions,
      handleAttachVideo,
      fileLoader,
      elapsedTime,
      progress,
      formattedVideoMaxDuration,
      acceptedVideoTypes,
      downloadableRecordedVideoElement,
      userInformationNeeded,
      isIframed,
      showGuidelines,
      iframeParentIsAdminApp,
      clickWrapFeatureEnabled,
    };
  },
};
</script>

<style lang="scss" scoped>
.desktop {
  align-items: center;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  flex: 1;
  min-width: 50vw;

  .terms-and-conditions-container {
    margin: 1.5rem auto 0;
  }

  .video-container {
    background: $black;
    border-radius: 0.5rem;
    height: 100%;
    position: relative;
    width: 100%;
    // padding-top: 56.25%;

    .countdown-overlay {
      background: rgba(255, 255, 255, 0.25);
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 3;

      .countdown-number {
        font-size: 2rem;
        background: rgba(255, 255, 255, 0.75);
        box-shadow: rgba(0, 0, 0, 0.3) 0 0.0625rem 0.1875rem;
        padding: 0.25rem;
        border-radius: 50%;
        height: 4rem;
        width: 4rem;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 1.5rem;
      }

      button {
        z-index: 1;
      }
    }

    .elapsed-time-overlay {
      align-items: center;
      background-color: rgba($white, 0.1);
      border-radius: 0.5rem;
      box-shadow: rgba(0, 0, 0, 0.3) 0 0.0625rem 0.1875rem;
      color: $white;
      display: flex;
      justify-content: center;
      left: 1rem;
      padding: 0.5rem;
      position: absolute;
      top: 1rem;
      z-index: 1;

      svg {
        height: 1.25rem;
        width: 1.25rem;
        margin-right: 0.5rem;
      }

      span {
        font-feature-settings: 'tnum';
        font-variant-numeric: tabular-nums;
        font-family: sans-serif;
      }
    }

    .interactions-overlay {
      position: absolute;
      bottom: 0;
      width: 100%;
      z-index: 2;

      .circular-progress {
        height: 4rem;
        width: auto;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        max-height: 4rem;
        max-width: 4rem;
      }

      .interactions-overlay-cog,
      .interactions-overlay-circle,
      .interactions-overlay-square,
      .interactions-overlay-attach {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        bottom: 1rem;
        cursor: pointer;
        background: rgba(255, 255, 255, 0.25);
        box-shadow: rgba(0, 0, 0, 0.3) 0 0.0625rem 0.1875rem;
        padding: 0.5rem;
        border-radius: 50%;
        transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out,
          box-shadow 0.15s ease-in-out, opacity 0.15s ease-in-out;
        z-index: 1;
        opacity: 0.85;

        &:hover {
          opacity: 1;

          .icon {
            opacity: 1;
          }
        }
      }

      .interactions-overlay-cog,
      .interactions-overlay-circle,
      .interactions-overlay-attach {
        overflow: hidden;
      }

      .interactions-overlay-cog {
        left: 1rem;
      }

      .interactions-overlay-circle,
      .interactions-overlay-square {
        bottom: 1.25rem;
        left: 50%;
        transform: translateX(-50%);
      }

      .interactions-overlay-attach {
        right: 1rem;
      }

      .icon {
        color: $white;
        height: 1.25rem;
        width: 1.25rem;
        opacity: 0.85;

        &.circle,
        &.square {
          color: $crimson;
          height: 2rem;
          width: 2rem;
        }

        &.cog,
        &.paperclip {
          filter: drop-shadow(0 0 0 rgba(0, 0, 0, 0.75));
        }
      }

      > button {
        border: none;
        appearance: none;
      }
    }

    @media (min-width: $large-screen) {
      flex-direction: row;
      justify-content: space-between;
    }

    .placeholder-video,
    .recorded-video {
      border-radius: 0.5rem;
      box-shadow: 0 0 0 0.0625rem $light-grey;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
    }

    .device-options-container {
      display: flex;
      flex-direction: column;
      position: absolute;
      left: 1rem;
      bottom: 4rem;
      z-index: 1;

      > * + * {
        margin-top: 1rem;
      }

      .device-select {
        align-items: center;
        display: flex;
        flex: 1;
        padding-right: 0.5rem;
        position: relative;

        &:after {
          content: '';
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 8px solid #303030;
          position: absolute;
          top: 50%;
          right: 16px;
          transform: scale(0.65) translateY(-50%);
        }

        select {
          padding-right: 1rem;
        }
      }

      .icon-container {
        display: inline-block;
        border-radius: 50%;
        height: 2.3rem;
        width: 2.3rem;
        overflow: hidden;
        margin-right: 1rem;

        .icon {
          overflow: hidden;
          color: white;
          padding: 0.65rem;
          box-shadow: rgba(0, 0, 0, 0.3) 0 0.0625rem 0.1875rem;
          width: 1rem;
          height: 1rem;
          opacity: 1;

          &.video {
            background: $secondary;
          }

          &.microphone {
            background: $purple;
          }
        }
      }
    }

    .ellipsis-overlay-menu {
      display: flex;
      flex-direction: column;
      position: absolute;
      right: 1rem;
      bottom: 4rem;
      z-index: 1;

      > * + * {
        margin-top: 1rem;
      }

      .option {
        align-items: center;
        display: flex;

        button {
          background: $white;
          border-radius: 0.25rem;
          color: $black;
          font-size: 14px;
          font-weight: normal;
          padding: 0.625rem;

          @media (min-width: $mobile) {
            font-size: 16px;
          }
        }
      }
    }
  }

  .button-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 36rem;
    margin: 0 auto;

    > * + * {
      margin-left: 0;
    }

    button,
    a {
      margin-top: 1rem;

      &.download {
        @media (min-width: $tablet) {
          padding: 13px 17px;
        }
      }
    }

    @media (min-width: $tablet) {
      flex-direction: row;
      width: auto;
      margin: 0.5rem 0 0;
      flex-wrap: wrap;
      justify-content: space-evenly;

      > * + * {
        margin-left: 1rem;
        margin-top: 0;
      }

      button {
        margin-top: 1rem;
        width: auto;
      }
    }

    @media (min-width: $small-screen) {
      max-width: 100%;

      > * + * {
        margin-left: 1rem;
      }
    }
  }
}
</style>
